import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Filter, 
  Search, 
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Globe,
  Star,
  ChevronLeft,
  ChevronRight,
  Bell
} from 'lucide-react';
import { useAppStore } from '../store';
import { cn } from '../utils';

interface EconomicEvent {
  id: string;
  title: string;
  country: string;
  currency: string;
  time: string;
  impact: 'High' | 'Medium' | 'Low';
  actual?: string;
  forecast?: string;
  previous?: string;
  category: string;
  description: string;
}

const Calendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedImpact, setSelectedImpact] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock economic events data
  const [events] = useState<EconomicEvent[]>([
    {
      id: '1',
      title: 'Non-Farm Payrolls',
      country: 'United States',
      currency: 'USD',
      time: '13:30',
      impact: 'High',
      actual: '199K',
      forecast: '180K',
      previous: '150K',
      category: 'Employment',
      description: 'Monthly change in the number of employed people during the previous month'
    },
    {
      id: '2',
      title: 'ECB Interest Rate Decision',
      country: 'European Union',
      currency: 'EUR',
      time: '12:15',
      impact: 'High',
      forecast: '4.50%',
      previous: '4.50%',
      category: 'Interest Rates',
      description: 'European Central Bank monetary policy decision'
    },
    {
      id: '3',
      title: 'GDP Growth Rate',
      country: 'United Kingdom',
      currency: 'GBP',
      time: '09:30',
      impact: 'High',
      actual: '0.6%',
      forecast: '0.4%',
      previous: '0.2%',
      category: 'GDP',
      description: 'Quarterly gross domestic product growth rate'
    },
    {
      id: '4',
      title: 'Consumer Price Index',
      country: 'Japan',
      currency: 'JPY',
      time: '23:30',
      impact: 'Medium',
      forecast: '3.2%',
      previous: '3.3%',
      category: 'Inflation',
      description: 'Monthly change in the price of goods and services'
    },
    {
      id: '5',
      title: 'Retail Sales',
      country: 'Canada',
      currency: 'CAD',
      time: '13:30',
      impact: 'Medium',
      actual: '0.8%',
      forecast: '0.5%',
      previous: '0.3%',
      category: 'Consumer Spending',
      description: 'Monthly change in the total value of sales at the retail level'
    },
    {
      id: '6',
      title: 'Manufacturing PMI',
      country: 'China',
      currency: 'CNY',
      time: '02:00',
      impact: 'Medium',
      forecast: '50.2',
      previous: '49.8',
      category: 'Manufacturing',
      description: 'Purchasing Managers Index for the manufacturing sector'
    }
  ]);

  const countries = ['all', 'United States', 'European Union', 'United Kingdom', 'Japan', 'Canada', 'China'];
  const impacts = ['all', 'High', 'Medium', 'Low'];

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesImpact = selectedImpact === 'all' || event.impact === selectedImpact;
    const matchesCountry = selectedCountry === 'all' || event.country === selectedCountry;
    return matchesSearch && matchesImpact && matchesCountry;
  });

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High': return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'Medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'Low': return 'text-green-400 bg-green-500/20 border-green-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const getCountryFlag = (country: string) => {
    const flags: { [key: string]: string } = {
      'United States': '🇺🇸',
      'European Union': '🇪🇺',
      'United Kingdom': '🇬🇧',
      'Japan': '🇯🇵',
      'Canada': '🇨🇦',
      'China': '🇨🇳'
    };
    return flags[country] || '🌍';
  };

  const getActualColor = (actual?: string, forecast?: string) => {
    if (!actual || !forecast) return 'text-gray-400';
    const actualNum = parseFloat(actual.replace(/[^0-9.-]/g, ''));
    const forecastNum = parseFloat(forecast.replace(/[^0-9.-]/g, ''));
    if (actualNum > forecastNum) return 'text-green-400';
    if (actualNum < forecastNum) return 'text-red-400';
    return 'text-yellow-400';
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setLastUpdate(new Date());
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Economic Calendar</h1>
          <p className="text-gray-400">Track important economic events and market-moving announcements</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all duration-200",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            Refresh
          </button>
          
          <div className="text-sm text-gray-400">
            Last update: {lastUpdate.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Calendar Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-400">Total Events</span>
          </div>
          <p className="text-xl font-bold text-white">{events.length}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-gray-400">High Impact</span>
          </div>
          <p className="text-xl font-bold text-red-400">
            {events.filter(event => event.impact === 'High').length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-sm text-gray-400">Beat Forecast</span>
          </div>
          <p className="text-xl font-bold text-green-400">
            {events.filter(event => {
              if (!event.actual || !event.forecast) return false;
              const actual = parseFloat(event.actual.replace(/[^0-9.-]/g, ''));
              const forecast = parseFloat(event.forecast.replace(/[^0-9.-]/g, ''));
              return actual > forecast;
            }).length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-gray-400">Countries</span>
          </div>
          <p className="text-xl font-bold text-purple-400">
            {new Set(events.map(event => event.country)).size}
          </p>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateDate('prev')}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous Day
          </button>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">{formatDate(selectedDate)}</h2>
            <p className="text-sm text-gray-400">Economic Events Schedule</p>
          </div>
          
          <button
            onClick={() => navigateDate('next')}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
          >
            Next Day
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Impact Filter */}
          <select
            value={selectedImpact}
            onChange={(e) => setSelectedImpact(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {impacts.map(impact => (
              <option key={impact} value={impact} className="bg-slate-800">
                {impact === 'all' ? 'All Impact Levels' : `${impact} Impact`}
              </option>
            ))}
          </select>
          
          {/* Country Filter */}
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {countries.map(country => (
              <option key={country} value={country} className="bg-slate-800">
                {country === 'all' ? 'All Countries' : country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {filteredEvents.map((event, index) => (
          <div key={event.id} className="glass-card p-6 hover:bg-white/5 transition-all duration-200">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Time and Impact */}
              <div className="flex items-center gap-4 lg:w-48">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-white font-medium">{event.time}</span>
                </div>
                
                <div className={cn(
                  "px-2 py-1 rounded border text-xs font-medium",
                  getImpactColor(event.impact)
                )}>
                  {event.impact}
                </div>
              </div>
              
              {/* Event Details */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{getCountryFlag(event.country)}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{event.title}</h3>
                    <p className="text-sm text-gray-400">{event.country} • {event.currency} • {event.category}</p>
                  </div>
                </div>
                
                <p className="text-gray-300 text-sm mb-3">{event.description}</p>
                
                {/* Data Values */}
                <div className="grid grid-cols-3 gap-4">
                  {event.actual && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Actual</p>
                      <p className={cn("font-bold", getActualColor(event.actual, event.forecast))}>
                        {event.actual}
                      </p>
                    </div>
                  )}
                  
                  {event.forecast && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Forecast</p>
                      <p className="font-bold text-blue-400">{event.forecast}</p>
                    </div>
                  )}
                  
                  {event.previous && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Previous</p>
                      <p className="font-bold text-gray-300">{event.previous}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <Star className="w-4 h-4 text-gray-400 hover:text-yellow-400" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <Bell className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredEvents.length === 0 && (
        <div className="glass-card p-12 text-center">
          <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No events found</h3>
          <p className="text-gray-400">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default Calendar;